import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { DrawerBodyWrapper, DrawerFooterWrapper } from '@base'
import { CRAN_SECTION_TYPES } from '@constants'
import { getCranReportById } from '@data/conn/get/cranReport'
import { showLoader } from '@data/state/action'
import { useForceUpdate } from '@hooks'
import { addCommentInCranReport } from '@data/conn/post/cranReport'
import { InputWrapper } from '../styles'
import ProbeTextArea from '../Components/ProbeTextArea'

const dataToForm = (prevState, data) => {
	if (data === null) {
		return prevState
	}
	let _obj = data?.slice(-1)?.[0]
	return {
		...prevState,
		value: _obj?.comment,
		show_in_cran: _obj?.show_in_cran,
		comment_type: _obj?.comment_type,
		dirty: true,
	}
}

const deriveFormState = (data) => {
	const state = {
		auditor: {
			label: 'Auditor Comment',
			placeholder: 'Enter comments here...',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		general_financials: {
			label: 'General Comments on Financials',
			placeholder: 'Enter general comments on financials',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		revenue_bifurcation: {
			label: 'Revenue and Revenue Bifurcation',
			placeholder: 'Enter comments here...',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		profitability: {
			label: 'Profitability',
			placeholer: 'Enter comments here',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		leverage: {
			label: 'Leverage',
			placeholer: 'Enter comments here',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		coverage: {
			label: 'Coverage',
			placeholer: 'Enter comments here',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		liquidity: {
			label: 'Liquidity',
			placeholer: 'Enter comments here',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		cash_flows: {
			label: 'Cash Flows',
			placeholer: 'Enter comments here',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		forex_exposure: {
			label: 'Foreign Exchange Exposure',
			placeholer: 'Enter comments here',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		interest_rate_sensitivity: {
			label: 'Interest Rate Sensitivity',
			placeholer: 'Enter comments here',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		operating_cycle: {
			label: 'Operating Cycle',
			placeholer: 'Enter comments here',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		interest_rate_risk: {
			label: 'Interest Rate Risk',
			placeholer: 'Enter comments here',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		fund_access: {
			label: 'Access to finance and other means of fund',
			placeholer: 'Enter comments here',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		forex_risk: {
			label: 'Forex Risk',
			placeholer: 'Enter comments here',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		accounting_quality: {
			label: 'Accounting Quality',
			placeholer: 'Enter comments here',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		contingent_liabilities: {
			label: 'Contingent Liabilities',
			placeholer: 'Enter comments here',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
	}
	if (data) {
		state.auditor = dataToForm(state.auditor, data?.auditor)
		state.general_financials = dataToForm(state.general_financials, data?.general_financials)
		state.revenue_bifurcation = dataToForm(state.revenue_bifurcation, data?.revenue_bifurcation)
		state.profitability = dataToForm(state.profitability, data?.profitability)
		state.leverage = dataToForm(state.leverage, data?.leverage)
		state.coverage = dataToForm(state.coverage, data?.coverage)
		state.liquidity = dataToForm(state.liquidity, data?.liquidity)
		state.cash_flows = dataToForm(state.cash_flows, data?.cash_flows)
		state.forex_exposure = dataToForm(state.forex_exposure, data?.forex_exposure)
		state.interest_rate_sensitivity = dataToForm(state.interest_rate_sensitivity, data?.interest_rate_sensitivity)
		state.operating_cycle = dataToForm(state.operating_cycle, data?.operating_cycle)
		state.interest_rate_risk = dataToForm(state.interest_rate_risk, data?.interest_rate_risk)
		state.fund_access = dataToForm(state.fund_access, data?.fund_access)
		state.forex_risk = dataToForm(state.forex_risk, data?.forex_risk)
		state.accounting_quality = dataToForm(state.accounting_quality, data?.accounting_quality)
		state.contingent_liabilities = dataToForm(state.contingent_liabilities, data?.contingent_liabilities)
	}

	return state
}

const FinancialsRiskForm = ({ probeId }) => {
	const dispatch = useDispatch()
	const [formState, setFormState] = useState(deriveFormState())
	const loggedInUser = useSelector((state) => state.getIn(['user', 'user']))
	const [forceUpdate, update] = useForceUpdate()

	useEffect(() => {
		dispatch(showLoader(true))
		getCranReportById(probeId, [CRAN_SECTION_TYPES.FINANCIALS])
			.then((response) => {
				setFormState(deriveFormState(response))
			})
			.catch((error) => {
				console.log(error)
			})
			.finally(() => {
				dispatch(showLoader(false))
			})
	}, [forceUpdate])

	const updateState = (key) => (value) => {
		setFormState((_s) => ({
			..._s,
			[key]: {
				..._s[key],
				value: value,
			},
		}))
	}

	const updateCheckHandler = (key) => (val) => {
		setFormState((_s) => ({
			..._s,
			[key]: {
				..._s[key],
				show_in_cran: val,
			},
		}))
	}

	const commentTypeHandler = (key) => (value) => {
		setFormState((_s) => ({
			..._s,
			[key]: {
				..._s[key],
				comment_type: value,
			},
		}))
	}

	const addHandler = (key) => () => {
		let obj = formState[key]
		let comment = {
			comment: obj.value,
			comment_type: obj.comment_type,
			created_by: loggedInUser?.user_id,
			show_in_cran: obj?.show_in_cran,
			// created_at: new Date().getTime(),
		}
		dispatch(showLoader(true))
		addCommentInCranReport({
			userId: loggedInUser?.user_id,
			commentValue: comment,
			commentKey: key,
			probe42Id: probeId,
		})
			.then((response) => {})
			.catch((error) => {})
			.finally(() => {
				update()
				dispatch(showLoader(false))
			})
	}

	return (
		<>
			<DrawerBodyWrapper>
				<InputWrapper>
					<ProbeTextArea
						label={formState?.auditor?.label}
						placeholder={formState?.auditor?.placeholder}
						toggleState={formState?.auditor?.comment_type}
						textValue={formState?.auditor?.value}
						onTextChange={updateState('auditor')}
						onCheckHandler={updateCheckHandler('auditor')}
						checkValue={formState?.auditor?.show_in_cran}
						onToggleChange={commentTypeHandler('auditor')}
						onButtonClick={addHandler('auditor')}
						disabled={formState?.auditor?.disabled}
					/>
				</InputWrapper>
				<InputWrapper>
					<ProbeTextArea
						label={formState?.general_financials?.label}
						placeholder={formState?.general_financials?.placeholder}
						toggleState={formState?.general_financials?.comment_type}
						textValue={formState?.general_financials?.value}
						onTextChange={updateState('general_financials')}
						onCheckHandler={updateCheckHandler('general_financials')}
						checkValue={formState?.general_financials?.show_in_cran}
						onToggleChange={commentTypeHandler('general_financials')}
						onButtonClick={addHandler('general_financials')}
						disabled={formState?.general_financials?.disabled}
					/>
				</InputWrapper>
				<InputWrapper>
					<ProbeTextArea
						label={formState?.revenue_bifurcation?.label}
						placeholder={formState?.revenue_bifurcation?.placeholder}
						toggleState={formState?.revenue_bifurcation?.comment_type}
						textValue={formState?.revenue_bifurcation?.value}
						onTextChange={updateState('revenue_bifurcation')}
						onCheckHandler={updateCheckHandler('revenue_bifurcation')}
						checkValue={formState?.revenue_bifurcation?.show_in_cran}
						onToggleChange={commentTypeHandler('revenue_bifurcation')}
						onButtonClick={addHandler('revenue_bifurcation')}
						disabled={formState?.revenue_bifurcation?.disabled}
					/>
				</InputWrapper>
				<InputWrapper>
					<ProbeTextArea
						label={formState?.profitability?.label}
						placeholder={formState?.profitability?.placeholder}
						toggleState={formState?.profitability?.comment_type}
						textValue={formState?.profitability?.value}
						onTextChange={updateState('profitability')}
						onCheckHandler={updateCheckHandler('profitability')}
						checkValue={formState?.profitability?.show_in_cran}
						onToggleChange={commentTypeHandler('profitability')}
						onButtonClick={addHandler('profitability')}
						disabled={formState?.profitability?.disabled}
					/>
				</InputWrapper>
				<InputWrapper>
					<ProbeTextArea
						label={formState?.leverage?.label}
						placeholder={formState?.leverage?.placeholder}
						toggleState={formState?.leverage?.comment_type}
						textValue={formState?.leverage?.value}
						onTextChange={updateState('leverage')}
						onCheckHandler={updateCheckHandler('leverage')}
						checkValue={formState?.leverage?.show_in_cran}
						onToggleChange={commentTypeHandler('leverage')}
						onButtonClick={addHandler('leverage')}
						disabled={formState?.leverage?.disabled}
					/>
				</InputWrapper>
				<InputWrapper>
					<ProbeTextArea
						label={formState?.coverage?.label}
						placeholder={formState?.coverage?.placeholder}
						toggleState={formState?.coverage?.comment_type}
						textValue={formState?.coverage?.value}
						onTextChange={updateState('coverage')}
						onCheckHandler={updateCheckHandler('coverage')}
						checkValue={formState?.coverage?.show_in_cran}
						onToggleChange={commentTypeHandler('coverage')}
						onButtonClick={addHandler('coverage')}
						disabled={formState?.coverage?.disabled}
					/>
				</InputWrapper>
				<InputWrapper>
					<ProbeTextArea
						label={formState?.liquidity?.label}
						placeholder={formState?.liquidity?.placeholder}
						toggleState={formState?.liquidity?.comment_type}
						textValue={formState?.liquidity?.value}
						onTextChange={updateState('liquidity')}
						onCheckHandler={updateCheckHandler('liquidity')}
						checkValue={formState?.liquidity?.show_in_cran}
						onToggleChange={commentTypeHandler('liquidity')}
						onButtonClick={addHandler('liquidity')}
						disabled={formState?.liquidity?.disabled}
					/>
				</InputWrapper>
				<InputWrapper>
					<ProbeTextArea
						label={formState?.cash_flows?.label}
						placeholder={formState?.cash_flows?.placeholder}
						toggleState={formState?.cash_flows?.comment_type}
						textValue={formState?.cash_flows?.value}
						onTextChange={updateState('cash_flows')}
						onCheckHandler={updateCheckHandler('cash_flows')}
						checkValue={formState?.cash_flows?.show_in_cran}
						onToggleChange={commentTypeHandler('cash_flows')}
						onButtonClick={addHandler('cash_flows')}
						disabled={formState?.cash_flows?.disabled}
					/>
				</InputWrapper>
				<InputWrapper>
					<ProbeTextArea
						label={formState?.forex_exposure?.label}
						placeholder={formState?.forex_exposure?.placeholder}
						toggleState={formState?.forex_exposure?.comment_type}
						textValue={formState?.forex_exposure?.value}
						onTextChange={updateState('forex_exposure')}
						onCheckHandler={updateCheckHandler('forex_exposure')}
						checkValue={formState?.forex_exposure?.show_in_cran}
						onToggleChange={commentTypeHandler('forex_exposure')}
						onButtonClick={addHandler('forex_exposure')}
						disabled={formState?.forex_exposure?.disabled}
					/>
				</InputWrapper>
				<InputWrapper>
					<ProbeTextArea
						label={formState?.interest_rate_sensitivity?.label}
						placeholder={formState?.interest_rate_sensitivity?.placeholder}
						toggleState={formState?.interest_rate_sensitivity?.comment_type}
						textValue={formState?.interest_rate_sensitivity?.value}
						onTextChange={updateState('interest_rate_sensitivity')}
						onCheckHandler={updateCheckHandler('interest_rate_sensitivity')}
						checkValue={formState?.interest_rate_sensitivity?.show_in_cran}
						onToggleChange={commentTypeHandler('interest_rate_sensitivity')}
						onButtonClick={addHandler('interest_rate_sensitivity')}
						disabled={formState?.interest_rate_sensitivity?.disabled}
					/>
				</InputWrapper>
				<InputWrapper>
					<ProbeTextArea
						label={formState?.operating_cycle?.label}
						placeholder={formState?.operating_cycle?.placeholder}
						toggleState={formState?.operating_cycle?.comment_type}
						textValue={formState?.operating_cycle?.value}
						onTextChange={updateState('operating_cycle')}
						onCheckHandler={updateCheckHandler('operating_cycle')}
						checkValue={formState?.operating_cycle?.show_in_cran}
						onToggleChange={commentTypeHandler('operating_cycle')}
						onButtonClick={addHandler('operating_cycle')}
						disabled={formState?.operating_cycle?.disabled}
					/>
				</InputWrapper>
				<InputWrapper>
					<ProbeTextArea
						label={formState?.interest_rate_risk?.label}
						placeholder={formState?.interest_rate_risk?.placeholder}
						toggleState={formState?.interest_rate_risk?.comment_type}
						textValue={formState?.interest_rate_risk?.value}
						onTextChange={updateState('interest_rate_risk')}
						onCheckHandler={updateCheckHandler('interest_rate_risk')}
						checkValue={formState?.interest_rate_risk?.show_in_cran}
						onToggleChange={commentTypeHandler('interest_rate_risk')}
						onButtonClick={addHandler('interest_rate_risk')}
						disabled={formState?.interest_rate_risk?.disabled}
					/>
				</InputWrapper>
				<InputWrapper>
					<ProbeTextArea
						label={formState?.fund_access?.label}
						placeholder={formState?.fund_access?.placeholder}
						toggleState={formState?.fund_access?.comment_type}
						textValue={formState?.fund_access?.value}
						onTextChange={updateState('fund_access')}
						onCheckHandler={updateCheckHandler('fund_access')}
						checkValue={formState?.fund_access?.show_in_cran}
						onToggleChange={commentTypeHandler('fund_access')}
						onButtonClick={addHandler('fund_access')}
						disabled={formState?.fund_access?.disabled}
					/>
				</InputWrapper>
				<InputWrapper>
					<ProbeTextArea
						label={formState?.forex_risk?.label}
						placeholder={formState?.forex_risk?.placeholder}
						toggleState={formState?.forex_risk?.comment_type}
						textValue={formState?.forex_risk?.value}
						onTextChange={updateState('forex_risk')}
						onCheckHandler={updateCheckHandler('forex_risk')}
						checkValue={formState?.forex_risk?.show_in_cran}
						onToggleChange={commentTypeHandler('forex_risk')}
						onButtonClick={addHandler('forex_risk')}
						disabled={formState?.forex_risk?.disabled}
					/>
				</InputWrapper>
				<InputWrapper>
					<ProbeTextArea
						label={formState?.accounting_quality?.label}
						placeholder={formState?.accounting_quality?.placeholder}
						toggleState={formState?.accounting_quality?.comment_type}
						textValue={formState?.accounting_quality?.value}
						onTextChange={updateState('accounting_quality')}
						onCheckHandler={updateCheckHandler('accounting_quality')}
						checkValue={formState?.accounting_quality?.show_in_cran}
						onToggleChange={commentTypeHandler('accounting_quality')}
						onButtonClick={addHandler('accounting_quality')}
						disabled={formState?.accounting_quality?.disabled}
					/>
				</InputWrapper>
				<InputWrapper>
					<ProbeTextArea
						label={formState?.contingent_liabilities?.label}
						placeholder={formState?.contingent_liabilities?.placeholder}
						toggleState={formState?.contingent_liabilities?.comment_type}
						textValue={formState?.contingent_liabilities?.value}
						onTextChange={updateState('contingent_liabilities')}
						onCheckHandler={updateCheckHandler('contingent_liabilities')}
						checkValue={formState?.contingent_liabilities?.show_in_cran}
						onToggleChange={commentTypeHandler('contingent_liabilities')}
						onButtonClick={addHandler('contingent_liabilities')}
						disabled={formState?.contingent_liabilities?.disabled}
					/>
				</InputWrapper>
			</DrawerBodyWrapper>
			<DrawerFooterWrapper></DrawerFooterWrapper>
		</>
	)
}

export default FinancialsRiskForm
